import React, { useCallback, useEffect, useState } from 'react';
import type { FC } from 'react';

import { useCreateFieldMutation, useUpdateFieldMutation } from '@/api/queries/field';
import { useCreateLinkedLayer, useDeleteLinkedLayer } from '@/api/queries/linked-layer';
import { type Field, FieldType, type LinkedLayer } from '@/api/types';
import { Button, ButtonType } from '@/common/button';
import { CheckBox } from '@/common/check-box';
import { Counter } from '@/common/counter';
import { FloatingPanel, FloatingPanelItem, FloatingPanelType } from '@/common/floating-panel';
import { TextField, TextFieldType } from '@/common/text-field';
import { TextIcon } from '@/common/text-icon';
import { toDecimal } from '@/utils';

import { useTemplateEditorStore } from '../store';
import { FieldsPanelView, type Layer, type TextLayerProps } from '../types';
import { LayerCard } from './layer-card';

const CHAR_LIMIT_DEFAULT_VALUE = 120;
const CHAR_LIMIT_MAX_VALUE = 1000;

export interface TextFieldEditViewProps {
    field: Field | null;
}

export const TextFieldEditView: FC<TextFieldEditViewProps> = ({ field }) => {
    const fields = useTemplateEditorStore((s) => s.template?.latestVersion.fields);
    const templateVersionId = useTemplateEditorStore((s) => s.template?.latestVersion.id);
    const width = useTemplateEditorStore((s) => s.fieldsPanelWidth);
    const setFields = useTemplateEditorStore((s) => s.setFields);
    const setFieldsPanelView = useTemplateEditorStore((s) => s.setFieldsPanelView);
    const setEditedFieldData = useTemplateEditorStore((s) => s.setEditedFieldData);
    const setEditedFieldId = useTemplateEditorStore((s) => s.setEditedFieldId);
    const setTemplateEdited = useTemplateEditorStore((s) => s.setIsEdited);

    const [name, setName] = useState<string>(field?.name || '');
    const [linkedLayers, setLinkedLayers] = useState<LinkedLayer[]>(field?.linkedLayers || []);
    const [autoTextScaling, setAutoTextScaling] = useState<boolean>(field?.properties.autoTextScaling || false);
    const [enableCharLimit, setEnableCharLimit] = useState<boolean>(
        (field?.properties?.charLimit && field?.properties?.charLimit > 0) || false,
    );
    const [charLimit, setCharLimit] = useState<number>(field?.properties.charLimit || CHAR_LIMIT_DEFAULT_VALUE);

    const { mutate: createField } = useCreateFieldMutation({
        onSuccess: (data: Field) => {
            syncLinkedLayers(data.id as number).then((updatedLayers) => {
                data.linkedLayers = updatedLayers;
                setFields([...(fields || []), data]);
                setTemplateEdited();
                closeView();
            });
        },
    });
    const { mutate: updateField } = useUpdateFieldMutation({
        onSuccess: (data: Field) => {
            syncLinkedLayers(data.id as number).then((updatedLayers) => {
                data.linkedLayers = updatedLayers;
                const newFields = fields?.map((f) => (f.id === data.id ? data : f)) || [];
                setFields(newFields);
                setTemplateEdited();
                closeView();
            });
        },
    });
    const { mutateAsync: createLinkedLayer } = useCreateLinkedLayer();
    const { mutateAsync: deleteLinkedLayer } = useDeleteLinkedLayer();

    const syncLinkedLayers = async (fieldId: number) => {
        const originalLinkedLayers = field?.linkedLayers || [];
        const newLinkedLayers = linkedLayers.filter(
            (l) => !originalLinkedLayers.find((ol) => ol.layerUuid === l.layerUuid),
        );
        const deleteLinkedLayers = originalLinkedLayers.filter(
            (ol) => !linkedLayers.find((l) => l.layerUuid === ol.layerUuid),
        );

        const insertRequests = Promise.all(
            newLinkedLayers.map(async (_layer) => {
                // Create a new object to avoid weird mutation issues likely due to Zustand use of proxies
                const layer = Object.assign({}, _layer);
                layer.field = fieldId;
                return createLinkedLayer(layer, { onSuccess: () => setTemplateEdited() });
            }),
        );

        const removeRequests = Promise.all(
            deleteLinkedLayers.map(async (layer) =>
                deleteLinkedLayer(layer.id as number, { onSuccess: () => setTemplateEdited() }),
            ),
        );

        return await Promise.all([insertRequests, removeRequests]).then((data) => {
            const newLinkedLayersData = data[0];
            return originalLinkedLayers
                .concat(newLinkedLayersData)
                .filter((l) => !deleteLinkedLayers.find((dl) => dl.id === l.id));
        });
    };

    const getEditedFieldData = useCallback((): Field => {
        return {
            id: field?.id,
            name,
            type: FieldType.Text,
            templateVersion: templateVersionId as number,
            linkedLayers,
            properties: {
                autoTextScaling,
                charLimit: enableCharLimit ? charLimit : 0,
            },
        };
    }, [autoTextScaling, charLimit, enableCharLimit, field, linkedLayers, name, templateVersionId]);

    const handleNameChange = (val: string) => {
        setName(val);
    };

    const handleAutoTextScalingChange = (val: boolean) => {
        setAutoTextScaling(val);
    };

    const handleCharLimitChange = (val: number) => {
        setCharLimit(val);
    };

    const handleSave = () => {
        const editedField = getEditedFieldData();
        editedField.linkedLayers = undefined;
        editedField.id ? updateField(editedField) : createField(editedField);
    };

    const handleLayerDeletion = (layer: LinkedLayer) => {
        setLinkedLayers(linkedLayers.filter((l) => l.layerUuid !== layer.layerUuid));
    };

    const handleLayerSelection = useCallback((selectedLayer: Layer | null, lastSelectedLayer: Layer | null) => {
        if (
            selectedLayer &&
            selectedLayer !== lastSelectedLayer &&
            !linkedLayers.find((l) => l.layerUuid === selectedLayer.id || l.variant === selectedLayer.variantId)
        ) {
            const textProps = selectedLayer.props as TextLayerProps;
            const linkedLayer: LinkedLayer = {
                field: 0, // will be overridden at the time of saving
                variant: selectedLayer.variantId,
                layerUuid: selectedLayer.id,
                initialValue: textProps.text,
                name: textProps.text.substring(0, 255),
                posX: selectedLayer.rect.x,
                posY: selectedLayer.rect.y,
                width: selectedLayer.rect.width,
                height: selectedLayer.rect.height,
                fontFamily: textProps.fontFamily,
                fontWeight: textProps.fontWeight,
                fontStyle: textProps.fontStyle,
                fontSize: textProps.fontSize,
                lineHeight: toDecimal(textProps.lineHeight, 2),
                fill: textProps.fill,
            };
            setLinkedLayers([...linkedLayers, linkedLayer]);
        }
    }, [linkedLayers]);

    const isFieldValid = () => {
        if (!name || name.length === 0) {
            return false;
        }
        if (linkedLayers.length === 0) {
            return false;
        }
        if (enableCharLimit && (charLimit <= 0 || charLimit > CHAR_LIMIT_MAX_VALUE)) {
            return false;
        }
        return true;
    };

    const closeView = () => {
        setEditedFieldId(null);
        setEditedFieldData(null);
        setFieldsPanelView(FieldsPanelView.Default);
    };

    useEffect(() => {
        const initState = useTemplateEditorStore.getState();
        handleLayerSelection(initState.selectedLayer, null);
        return useTemplateEditorStore.subscribe(
            (s) => s.selectedLayer,
            handleLayerSelection
        );
    }, [handleLayerSelection]);

    // update edited field data when field prop changes
    useEffect(() => {
        setEditedFieldData(getEditedFieldData());
    }, [getEditedFieldData, setEditedFieldData]);

    return (
        <FloatingPanel
            className="pr-4 py-4 h-full flex-shrink-0 overflow-y-auto"
            type={FloatingPanelType.LIGHT}
            width={width}
        >
            <FloatingPanelItem className="flex flex-row justify-between items-center px-4">
                <div className="flex flex-row gap-2 items-center">
                    <TextIcon className="w-5 h-5 text-white" />
                    <p className="text-base text-white font-medium">New Text Field</p>
                </div>
                <button title="Cancel" className="text-white text-xs font-normal h-5" type="button" onClick={closeView}>
                    Cancel
                </button>
            </FloatingPanelItem>

            <FloatingPanelItem className="flex flex-col gap-3 h-full px-4">
                <div className="flex flex-col gap-1">
                    <p className="text-sm text-white">Field Name</p>
                    <TextField
                        id="fieldName"
                        type={TextFieldType.Text}
                        value={name}
                        onChange={handleNameChange}
                        placeholder="Enter Text Field Name"
                    />
                </div>
                <div className="flex flex-col gap-1">
                    <p className="text-sm text-white">Layers</p>
                    {!linkedLayers.length && (
                        <div className="p-2 bg-blue-60 bg-opacity-20">
                            <p className="text-xs text-blue-30">Select a text layer from any frame</p>
                        </div>
                    )}
                    {linkedLayers.map((layer) => (
                        <LayerCard
                            key={layer.layerUuid}
                            layer={layer}
                            onEdit={() => { }}
                            onDelete={() => handleLayerDeletion(layer)}
                        />
                    ))}
                </div>
                <div className="flex flex-col gap-1">
                    <p className="text-sm text-white">Style Control</p>
                    <CheckBox
                        className="mt-2"
                        label="Enable Text Scaling"
                        description="Automatically reduce font size if content does not fit text box."
                        checked={autoTextScaling}
                        onChange={handleAutoTextScalingChange}
                    />
                    <div className="flex flex-row justify-between items-center mt-2">
                        <CheckBox label="Set Character Limit" checked={enableCharLimit} onChange={setEnableCharLimit} />
                        <Counter disabled={!enableCharLimit} defaultValue={CHAR_LIMIT_DEFAULT_VALUE} min={1} max={CHAR_LIMIT_MAX_VALUE} value={charLimit} onChange={handleCharLimitChange} />
                    </div>
                </div>
                <Button title="Done" disabled={!isFieldValid()} className="mt-2" buttonType={ButtonType.PRIMARY} onClick={handleSave} />
            </FloatingPanelItem>
        </FloatingPanel>
    );
};
